/**
 * Aktueller Betrieb (Mandant) des eingeloggten Users.
 *
 * Die Datentrennung selbst macht die Datenbank per RLS — dieses Modul sorgt
 * nur dafür, dass die App weiß, in welchem Betrieb sie gerade arbeitet:
 * für Storage-Pfade (<betrieb_id>/…), die Anzeige und den Betriebswechsel.
 */

import { supabase } from './supabaseClient';

let aktuell = null;      // { id, name, gewerk, ist_demo, rolle }
let alle = [];           // alle Betriebe, in denen der User Mitglied ist
let istOpteroTeam = false;

export async function ladeBetriebe() {
    const [{ data: id }, { data: mitgliedschaften }, { data: admin }] = await Promise.all([
        supabase.rpc('aktueller_betrieb'),
        supabase.from('betrieb_mitglieder').select('rolle, betrieb_id, user_id, betriebe(id, name, gewerk, ist_demo, status)'),
        supabase.rpc('is_admin_team_member'),
    ]);
    const { data: { user } } = await supabase.auth.getUser();
    alle = (mitgliedschaften || [])
        .filter((m) => m.user_id === user?.id && m.betriebe)
        .map((m) => ({ ...m.betriebe, rolle: m.rolle }))
        .sort((a, b) => a.name.localeCompare(b.name, 'de'));
    aktuell = alle.find((b) => b.id === id) || null;
    istOpteroTeam = admin === true;
    return { aktuell, alle, istOpteroTeam };
}

export function aktiverBetrieb() { return aktuell; }
export function alleBetriebe() { return alle; }
export function betriebId() { return aktuell?.id || null; }
export function gehoertZumOpteroTeam() { return istOpteroTeam; }

/** Wechselt den aktiven Betrieb und lädt die App neu — alle Caches sind danach sauber. */
export async function wechsleBetrieb(id) {
    const { error } = await supabase.rpc('betrieb_wechseln', { p_betrieb: id });
    if (error) throw new Error(error.message);
    window.location.assign('/app/dashboard');
}

/** Storage-Pfad im Ordner des Betriebs. Ohne Präfix lehnt die Storage-RLS den Upload ab. */
export function speicherPfad(dateiname) {
    const id = betriebId();
    if (!id) throw new Error('Kein Betrieb aktiv — bitte neu anmelden.');
    return `${id}/${dateiname}`;
}

// ─── Demo-Betrieb "Tischlerei Nordsee" (fiktiv) ─────────────────────────────
// Feste ID aus Migration 20261008100_demo_betrieb.sql. Nur für das Optero-Team,
// erreichbar über den versteckten Schalter an der Versionszeile.

export const DEMO_BETRIEB_ID = '00000000-0000-4000-a000-000000000002';
// Interne Spielwiese des Teams (Bestand aus der Zeit vor den Mandanten)
export const OPTERO_DEMO_ID = '00000000-0000-4000-a000-000000000001';

/** Demo-Daten neu aufbauen — läuft serverseitig nur im Demo-Betrieb. */
export async function demoNeuAufbauen() {
    const { data, error } = await supabase.functions.invoke('seed-demo-data?action=seed-nordsee', { body: {} });
    if (error) {
        let meldung = error.message;
        try { meldung = (await error.context.json()).error || meldung; } catch (_) {}
        throw new Error(meldung);
    }
    if (!data?.ok) throw new Error(data?.error || 'Demo konnte nicht aufgebaut werden.');
    return data;
}

/** In den Demo-Betrieb wechseln (legt ihn beim ersten Mal an und befüllt ihn). */
export async function demoOeffnen() {
    const { error } = await supabase.rpc('demo_betrieb_oeffnen');
    if (error) throw new Error(error.message);
    const { count } = await supabase.from('projects').select('id', { count: 'exact', head: true });
    if (!count) await demoNeuAufbauen();
    window.location.assign('/app/dashboard');
}

// ─── Onboarding-Demo ────────────────────────────────────────────────────────
// Eigener Testbetrieb (Migration 20261009100_onboarding_demo.sql), getrennt von
// der Tischlerei Nordsee. Jeder Start leert ihn — danach läuft der Wizard wie
// bei einem echten Neukunden.

export const ONBOARDING_DEMO_ID = '00000000-0000-4000-a000-000000000003';

/**
 * Zeigt dieser Betrieb Beispielinhalte (erfundene Nachrichten, Musterpreise,
 * Hansen-Beispiele)? Nur die Pitch-Demos. Die Onboarding-Demo ist zwar ein
 * Testbetrieb, soll sich aber genau wie ein echter neuer Kunde verhalten.
 */
export function zeigtDemoInhalte(b) {
    return !!b?.ist_demo && b.id !== ONBOARDING_DEMO_ID;
}

export async function onboardingDemoStarten() {
    const { error } = await supabase.rpc('demo_onboarding_starten');
    if (error) throw new Error(error.message);
    window.location.assign('/app/dashboard');
}

/** Optero-Team: neuen Betrieb anlegen und Inhaber einladen (Edge Function betrieb-anlegen). */
export async function betriebAnlegen({ name, gewerk, inhaberEmail, istDemo }) {
    const { data, error } = await supabase.functions.invoke('betrieb-anlegen', {
        body: { name, gewerk, inhaber_email: inhaberEmail, ist_demo: !!istDemo },
    });
    if (error) {
        let meldung = error.message;
        try { meldung = (await error.context.json()).error || meldung; } catch (_) {}
        throw new Error(meldung);
    }
    if (data?.error) throw new Error(data.error);
    return data;
}
